import React, { useEffect, useState } from 'react';
import { useLiceos } from '../../../application/liceos/hooks/useLiceos';
import ToolbarComponent from '../../ui/section';
import DynamicTable from '../../ui/table2';
import Loader from '../../ui/loader';
import { IconButton } from '../../ui/button';
import FloatingButton from '../../ui/floatButton';
import { ModalConfirm } from '../../ui/modal2';
import './detail.css';

const LiceoDetailPage = (props) => {
    const { 
        getLiceoById, 
        getLiceoUsers,
        removeUserFromLiceo,
        isLoading, 
        error 
    } = useLiceos();
    
    const [liceo, setLiceo] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeModal, setRemoveModal] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        const id = props.match?.params?.id;
        if (id) {
            loadLiceo(id);
        }
    }, [props.match?.params?.id]);

    const loadLiceo = async (id) => {
        setLoading(true);
        try {
            // Cargar liceo y usuarios en paralelo
            const [liceoData, usersData] = await Promise.all([
                getLiceoById(id),
                getLiceoUsers(id)
            ]);
            
            console.log('📋 Datos del liceo recibidos:', liceoData);
            console.log('👥 Usuarios del liceo recibidos:', usersData);
            
            setLiceo(liceoData);
            
            // Los usuarios ya vienen completos del nuevo endpoint
            if (Array.isArray(usersData) && usersData.length > 0) {
                setUsers(usersData);
            } else {
                console.log('⚠️ No se encontraron usuarios en el liceo');
                setUsers([]);
            }
        } catch (err) {
            console.error('❌ Error al cargar liceo:', err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'Activo',
            inactive: 'Inactivo'
        };
        return labels[status] || status;
    };

    const getStatusColor = (status) => {
        const colors = {
            active: '#10B981',
            inactive: '#6B7280'
        };
        return colors[status] || '#6B7280';
    };

    const handleRemoveUser = (userId) => {
        setCurrentUserId(userId);
        setRemoveModal(true);
    };

    const handleRemoveUserConfirm = async () => {
        if (currentUserId && liceo) {
            try {
                await removeUserFromLiceo(liceo.id || liceo._id, currentUserId);
                await loadLiceo(liceo.id || liceo._id);
                setRemoveModal(false);
                setCurrentUserId(null);
            } catch (err) {
                console.error('Error al remover usuario:', err);
            }
        }
    };

    const handleAddUser = () => {
        props.history.push(`/academia/liceos/${props.match?.params?.id}/users/new`);
    };

    const handleViewUser = (userId) => {
        props.history.push(`/academia/liceos/${props.match?.params?.id}/users/${userId}`);
    };

    const handleEditUser = (userId) => {
        props.history.push(`/academia/liceos/${props.match?.params?.id}/users/${userId}/edit`);
    };

    const handleViewStats = () => {
        props.history.push(`/academia/liceos/${props.match?.params?.id}/stats`);
    };

    if (loading || isLoading) {
        return (
            <ToolbarComponent label="Detalle del Liceo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando liceo..." />
            </ToolbarComponent>
        );
    }

    if (error || !liceo) {
        return (
            <ToolbarComponent label="Detalle del Liceo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <div className="error-message">
                    {error || 'No se pudo cargar el liceo'}
                </div>
            </ToolbarComponent>
        );
    }

    const userColumns = [
        { key: 'name', label: 'Nombre', style: { width: "25%" } },
        { key: 'email', label: 'Email', style: { width: "25%" } },
        { key: 'userType', label: 'Tipo', style: { width: "15%" } },
        { key: 'actions', label: 'Acciones' }
    ];

    const userCustomElements = {
        name: (data) => {
            const fullName = `${data.firstName || data.name || ''} ${data.lastName || ''}`.trim();
            return <span>{fullName || '-'}</span>;
        },
        userType: (data) => {
            const types = {
                estudiante: 'Estudiante',
                docente: 'Docente',
                director: 'Director',
                coach: 'Coach'
            };
            return <span>{types[data.userType] || data.userType || '-'}</span>;
        },
        actions: (data) => (
            <div style={{ display: 'flex', gap: '8px' }}>
                <IconButton onClick={() => handleViewUser(data.id || data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => handleEditUser(data.id || data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16.04 3.02001L8.16 10.9C7.86 11.2 7.56 11.79 7.5 12.22L7.07 15.23C6.91 16.32 7.68 17.08 8.77 16.93L11.78 16.5C12.2 16.44 12.79 16.14 13.1 15.84L20.98 7.96001C22.34 6.60001 22.98 5.02001 20.98 3.02001C18.98 1.02001 17.4 1.66001 16.04 3.02001Z" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.91 4.1499C15.58 6.5399 17.45 8.4099 19.85 9.0899" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => handleRemoveUser(data.id || data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.33 16.5H13.66" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 12.5H14.5" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        )
    };

    return (
        <ToolbarComponent label="Detalle del Liceo" showBackButton={true} onBackClick={() => props.history.goBack()}>
            <div className="liceo-detail-container">
                {/* Header */}
                <div className="detail-header">
                    <div>
                        <h1 className="detail-title">{liceo.name || 'Sin nombre'}</h1>
                        <p className="detail-subtitle">{liceo.address || '-'}</p>
                    </div>
                    <div className="detail-actions">
                        <button 
                            onClick={handleViewStats}
                            style={{
                                padding: '8px 16px',
                                background: 'transparent',
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            Ver Estadísticas
                        </button>
                        <span 
                            className="status-badge"
                            style={{ 
                                background: `${getStatusColor(liceo.status)}20`,
                                color: getStatusColor(liceo.status)
                            }}
                        >
                            {getStatusLabel(liceo.status)}
                        </span>
                    </div>
                </div>

                {/* Información del Liceo */}
                <div className="detail-section">
                    <h2 className="section-title">Información General</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Nombre</span>
                            <span className="info-value">{liceo.name || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Dirección</span>
                            <span className="info-value">{liceo.address || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Región</span>
                            <span className="info-value">{liceo.region || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Ciudad</span>
                            <span className="info-value">{liceo.city || '-'}</span>
                        </div>
                        {liceo.createdAt && (
                            <div className="info-item">
                                <span className="info-label">Fecha de creación</span>
                                <span className="info-value">{formatDate(liceo.createdAt)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Usuarios del Liceo */}
                <div className="detail-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 className="section-title" style={{ margin: 0 }}>Usuarios ({users.length})</h2>
                    </div>
                    <DynamicTable
                        headers={userColumns}
                        data={users}
                        customElements={userCustomElements}
                        fullPage={false}
                        showLabel={false}
                        loaderComponent={<Loader text="Cargando usuarios" />}
                        isLoading={false}
                    />
                    <FloatingButton position="bottom-right" onClick={handleAddUser} />
                </div>
            </div>
            <ModalConfirm 
                isOpen={removeModal} 
                onClose={() => setRemoveModal(false)} 
                onAccept={handleRemoveUserConfirm}
                title="¿Remover usuario del liceo?"
                message="El usuario será removido de este liceo."
            />
        </ToolbarComponent>
    );
};

export default LiceoDetailPage;

